<template>
	<div>
		<div class="container hidden-xs">
			<b-breadcrumbs :items="breadcrumbs"/>
		</div>
		<section class="container">
			<div class="row">
				<div class="col-xs-12">
					<h1>Edit user</h1>
					<div class="page-summary-text">
						Edit details of {{ `${user.first_name} ${user.last_name}` }}
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-xs-12 col-sm-6">
					<h2>Details</h2>
					<label for="title">Title</label>
					<input
						v-model="user.title"
						type="text"
						name="title"
						class="form-control"
						placeholder="Title"
					/>
					<label for="firstName">First name</label>
					<label
						v-if="$v.user.first_name.$error && !$v.user.first_name.required"
						class="label error pull-right"
					>Required</label>
					<input
						v-model="user.first_name"
						:class="{ 'error': $v.user.first_name.$error }"
						type="text"
						name="firstName"
						class="form-control"
						placeholder="First name"
					/>
					<label for="lastName">Last name</label>
					<label
						v-if="$v.user.last_name.$error && !$v.user.last_name.required"
						class="label error pull-right"
					>Required</label>
					<input
						v-model="user.last_name"
						:class="{ 'error': $v.user.last_name.$error }"
						type="text"
						name="lastName"
						class="form-control"
						placeholder="Last name"
					/>
					<label for="email">Email address</label>
					<label
						v-if="$v.user.email_address.$error && !$v.user.email_address.required"
						class="label error pull-right"
					>Required</label>
					<input
						v-model="user.email_address"
						:class="{ 'error': $v.user.email_address.$error }"
						type="email"
						name="email"
						class="form-control"
						placeholder="Email address"
					/>
				</div>
				<div class="col-xs-12 col-sm-6">
					<h2>Attributes</h2>
					<div class="row">
						<div class="col-xs-6">
							<label for="switch">User level</label>
							<div class="switch" name="switch">
								<span class="toggle-switch">
									<input v-model="user.is_admin" id="isUser" name="isUser" type="checkbox">
									<label class="label-primary" for="isUser"/>
								</span>
								<label for="isUser" style="font-weight:normal;cursor:pointer;">
									{{ user.is_admin ? 'Admin' : 'User only' }}
								</label>
							</div>
						</div>
						<div class="col-xs-6 pull-right">
							<label for="reset">{{ passwordLockText }}</label><br/>
							<b-btn id="reset" class="btn-default" @click="lockPassword">
								Reset password
								<i class="material-icons">{{ lockPasswordFields ? 'lock' : 'lock_open' }}</i>
							</b-btn>
						</div>
					</div>
					<div class="row">
						<div class="col-xs-12">
							<label for="password">Password</label>
							<label
								v-if="$v.currentState.password.$error && !$v.currentState.password.required"
								class="label error pull-right"
							>Required</label>
							<label
								v-if="
									!$v.currentState.repeat_password.sameAs && $v.currentState.repeat_password.$error
								"
								class="label error pull-right"
							>Passwords must match</label>
							<input
								v-model="currentState.password"
								:class="{ 'error': $v.currentState.password.$error }"
								:disabled="lockPasswordFields"
								type="password"
								name="password"
								class="form-control"
								placeholder="Password (Must contain at least 6 characters)"
							/>
						</div>
					</div>
					<div class="row">
						<div class="col-xs-12">
							<label for="repeatPassword">Repeat password</label>
							<label
								v-if="
									!$v.currentState.repeat_password.required &&
									$v.currentState.repeat_password.$error
								"
								class="label error pull-right"
							>Required</label>
							<label
								v-if="
									!$v.currentState.repeat_password.sameAs && $v.currentState.repeat_password.$error
								"
								class="label error pull-right"
							>Passwords must match</label>
							<input
								v-model="currentState.repeat_password"
								:class="{ 'error': $v.currentState.repeat_password.$error }"
								:disabled="lockPasswordFields"
								type="password"
								name="repeatPassword"
								class="form-control"
								placeholder="Repeat password"
							/>
						</div>
					</div>
					<!--
					<div class="row">
						<div class="col-xs-12">
							<label for="regions">Regions</label>
							<b-multi-select
								v-model="currentState.regions"
								:options="regions"
								name="regions"
								class="multi-select region-selector"
								filterable
								block
								collapse-selected
							/>
						</div>
					</div>
					-->
				</div>
			</div>
			<hr>
			<div class="row">
				<div class="col-xs-12">
					<div class="container center">
						<router-link to="/admin/users">
							<b-btn class="btn-md btn-default">Cancel</b-btn>
						</router-link>
						&nbsp;&nbsp;&nbsp;
						<b-btn class="btn-md btn-primary" @click="saveUser">Save</b-btn>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style lang="less" scoped>
	.form-control {
		margin-bottom: 15px;
		border-radius: 4px !important;
	}
	.switch {
		margin-bottom: 15px;
	}
	.toggle-switch {
		margin-right: 5px;
	}
	.multi-select {
		margin-bottom: 15px;
	}
	.error {
		border-color: red;
		color: red;
	}
</style>

<script>
import { required, sameAs, minLength, requiredIf } from 'vuelidate/lib/validators';
import { map, flatten } from 'lodash/fp';
import { Notification } from 'uiv';
import { mapGetters } from 'vuex';

export default {
	created() {
		this.getCurrentState();
		this.getRegions();
	},
	data() {
		return {
			regions: [],
			currentState: {
				password: null,
				repeat_password: null,
				regions: []
			},
			lockPasswordFields: true
		};
	},
	validations: {
		user: {
			first_name: { required },
			last_name: { required },
			email_address: { required },
		},
		currentState: {
			password: {
				required: requiredIf(function isNotLocked() {
					return !this.lockPasswordFields;
				}),
				minLength: minLength(6),
			},
			repeat_password: {
				required: requiredIf(function isNotLocked() {
					return !this.lockPasswordFields;
				}),
				minLength: minLength(6),
				sameAsPassword: sameAs('password')
			}
		}
	},
	computed: {
		user() {
			return this.currentState;
		},
		passwordLockText() {
			if (this.lockPasswordFields) {
				return 'Unlock fields';
			}
			return 'Lock fields';
		},
		...mapGetters({ getBreadcrumbs: 'breadcrumbs' }),
		breadcrumbs() {
			return this.getBreadcrumbs;
		},
	},
	methods: {
		lockPassword() {
			if (this.lockPasswordFields) {
				this.lockPasswordFields = false;
			} else {
				this.lockPasswordFields = true;
				this.currentState.password = null;
				this.currentState.repeat_password = null;
			}
		},
		async getCurrentState() {
			const { id } = this.$route.params;
			const { data: { data: currentState } } = await this.$axios.get(`/user/${id}`);
			currentState.regions = map(state => state.id, currentState.regions);
			this.$set(this, 'currentState', currentState);
		},
		async getRegions() {
			const { data: { data } } = await this.$axios.get('/region');
			const regions = flatten(map(({ id, name }) => ({ value: id, label: name }), data));
			this.$set(this, 'regions', regions);
		},
		async saveUser() {
			if (this.$v.$invalid) {
				this.$v.$touch();
			} else {
				try {
					const { id } = this.$route.params;
					await this.$axios.put(`/user/${id}`, this.user);
					this.$router.push({ path: '/admin/users' });
					Notification.notify({
						type: 'success',
						title: `${this.user.first_name} ${this.user.last_name} edited successfully.`,
						duration: 10000
					});
				} catch (e) {
					Notification.notify({
						type: 'danger',
						title: 'Could not edit user.',
						content: e.response ? e.response.data.message : '',
						duration: 10000
					});
				}
			}
		}
	}
};
</script>
